import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './DonateModal.module.css';
import logoLetras from '../../assets/icons/logo-letras.png';

const DonateModal = ({ isOpen, onClose }) => {
  const [view, setView] = useState('selection'); // 'selection' or 'economic'
  const [copied, setCopied] = useState('');
  const modalRef = useRef(null);

  // Bloquear scroll al abrir
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setView('selection');
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Manejar focus trapping
  useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const copyToClipboard = (text, field) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        setCopied(field);
        setTimeout(() => setCopied(''), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopied(field);
          setTimeout(() => setCopied(''), 2000);
        } else {
          throw new Error('Fallback copy failed');
        }
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const WHATSAPP_NUMBER = '5493815244017';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={styles.overlay} onClick={onClose}>
          <motion.div
            ref={modalRef}
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="donate-modal-title"
            tabIndex="-1"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Cerrar modal"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>

            <div className={styles.content}>
              <div className={styles.header}>
                <img
                  src={logoLetras}
                  alt=""
                  className={styles.logo}
                  aria-hidden="true"
                  width={781}
                  height={554}
                />
                <h2 id="donate-modal-title" className={styles.title}>
                  ¡Súmate a la Fundación!
                </h2>
                <p className={styles.subtitle}>
                  {view === 'selection'
                    ? 'Tu aporte es fundamental para transformar nuestra ciudad.'
                    : 'Gracias por tu generosidad. Aquí tienes los datos para realizar tu transferencia.'}
                </p>
              </div>

              <div className={styles.container}>
                {view === 'selection' ? (
                  <div className={styles.grid}>
                    <div className={styles.mainActionCol}>
                      <button
                        className={styles.donateBtn}
                        onClick={() => setView('economic')}
                      >
                        <div className={styles.btnInner}>
                          <span
                            className={`${styles.btnIcon} material-symbols-outlined`}
                            aria-hidden="true"
                          >
                            payments
                          </span>
                          <div className={styles.btnText}>
                            <span className={styles.btnTitle}>
                              Donación Económica
                            </span>
                            <span className={styles.btnSubtitle}>
                              Transferencia bancaria
                            </span>
                          </div>
                        </div>
                        <span
                          className={`${styles.arrow} material-symbols-outlined`}
                          aria-hidden="true"
                        >
                          arrow_forward
                        </span>
                      </button>
                    </div>

                    <div className={styles.mainActionCol}>
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=%C2%A1Hola%20Fundaci%C3%B3n!%20Quisiera%20colaborar%20con%20una%20donaci%C3%B3n%20material.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.materialBtn}
                        style={{ textDecoration: 'none' }}
                      >
                        <div className={styles.btnInner}>
                          <span
                            className={`${styles.btnIcon} material-symbols-outlined`}
                            aria-hidden="true"
                          >
                            inventory_2
                          </span>
                          <div className={styles.btnText}>
                            <span className={styles.btnTitle}>
                              Donación Material
                            </span>
                            <span className={styles.btnSubtitle}>
                              Ropa, alimentos, etc.
                            </span>
                          </div>
                        </div>
                        <span
                          className={`${styles.arrow} material-symbols-outlined`}
                          aria-hidden="true"
                        >
                          arrow_forward
                        </span>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className={styles.economicView}>
                    <div className={styles.bankCard}>
                      <div className={styles.bankHeader}>
                        <span className={styles.bankName}>
                          FUNDACIÓN VALORES PARA MI CIUDAD
                        </span>
                        <p className={styles.bankDetail}>
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: '1.1rem' }}
                            aria-hidden="true"
                          >
                            badge
                          </span>
                          Cuit: 30716591545
                        </p>
                        <p className={styles.bankDetail}>
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: '1.1rem' }}
                            aria-hidden="true"
                          >
                            account_balance
                          </span>
                          Banco: SANTANDER RIO S.A.
                        </p>
                      </div>

                      <div className={styles.copyGrid}>
                        <div className={styles.copyItem}>
                          <span className={styles.copyLabel}>
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: '1.1rem' }}
                              aria-hidden="true"
                            >
                              account_balance_wallet
                            </span>
                            CBU
                          </span>
                          <div className={styles.copyValueRow}>
                            <code className={styles.code}>
                              0720069420000009388640
                            </code>
                            <button
                              className={styles.copyAction}
                              onClick={() =>
                                copyToClipboard('0720069420000009388640', 'cbu')
                              }
                              aria-label="Copiar CBU"
                            >
                              <span
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                {copied === 'cbu' ? 'done' : 'content_copy'}
                              </span>
                            </button>
                          </div>
                        </div>

                        <div className={styles.copyItem}>
                          <span className={styles.copyLabel}>
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: '1.1rem' }}
                              aria-hidden="true"
                            >
                              alternate_email
                            </span>
                            Alias
                          </span>
                          <div className={styles.copyValueRow}>
                            <code className={styles.code}>
                              FUNDACIONVALORES
                            </code>
                            <button
                              className={styles.copyAction}
                              onClick={() =>
                                copyToClipboard('FUNDACIONVALORES', 'alias')
                              }
                              aria-label="Copiar Alias"
                            >
                              <span
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                {copied === 'alias' ? 'done' : 'content_copy'}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.actions}>
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=%C2%A1Hola!%20Acabo%20de%20realizar%20una%20donaci%C3%B3n%20y%20quer%C3%ADa%20compartir%20el%20comprobante.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.whatsappBtn}
                      >
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          share
                        </span>
                        Compartir comprobante
                      </a>
                      <button
                        className={styles.backBtn}
                        onClick={() => setView('selection')}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }} aria-hidden="true">
                          arrow_back
                        </span>
                        Volver a opciones
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DonateModal;
